import { SHAPEFILE_NAME_LABELS_LAYER } from "constants/mapLayers.constants";
import mapboxgl from "mapbox-gl";

import { TileJSONResponse } from "api/shapefile";

import { IEditableProjectLayer } from "components/project/layers/hooks";
import { Shapefile } from "components/project/shapefiles/queries";
import {
  DEFAULT_SHAPEFILE_OPACITY,
  DEFAULT_SHAPEFILE_THICKNESS,
  type FillPattern,
  getDashArray,
  normalizeFillPattern,
  resolveShapefileFillPattern,
  resolveShapefileLineStyle,
  shapefileLayerType
} from "components/project/shapefiles/shapefiles.constants";
import type {
  FeatureStateStyle,
  ShapefileFeatureRecord
} from "components/project/shapefiles/shapefiles.types";

import { ensureFillPatternImage } from "../../../../utils/fillPatterns";
import { getMapboxLayerType, isLineGeometry } from "../../../../utils/geometryUtils";
import { setFilter, setPaintProperty } from "../../../../utils/layerOperations";
import { buildShapefilePointRadius } from "../../../../utils/shapefilePointRadius";
import { getColor } from "../getColor";
import { getBeforeIdForNewShapefileLayers } from "./getBeforeIdForNewShapefileLayers";

const DEFAULT_FILL_COLOR = "#3388ff";
const DEFAULT_STROKE_COLOR = "#000000";

/**
 * Build a Mapbox match expression that maps each feature's shapefileGeomId
 * to a pattern image rendered in that feature's own color.
 * Returns a match expression or a single image name string.
 */
function buildFillPatternExpression(
  mapbox: mapboxgl.Map,
  pattern: FillPattern,
  features: Shapefile["features"],
  fallbackColor: string
): mapboxgl.Expression | string | null {
  if (!features || features.length === 0) {
    return ensureFillPatternImage(mapbox, pattern, fallbackColor);
  }

  // Collect unique colors and register a pattern image for each
  const colorToImageName = new Map<string, string>();
  for (const f of features) {
    const featureColor = f.color ? getColor(f.color) : fallbackColor;
    if (!colorToImageName.has(featureColor)) {
      const imageName = ensureFillPatternImage(mapbox, pattern, featureColor);
      if (imageName) {
        colorToImageName.set(featureColor, imageName);
      }
    }
  }

  // If only one unique color, no need for a match expression
  if (colorToImageName.size <= 1) {
    return colorToImageName.values().next().value ?? null;
  }

  // Build match expression: ["match", ["get", "shapefileGeomId"], id1, img1, id2, img2, ..., fallbackImg]
  const matchEntries: (string | mapboxgl.Expression)[] = [
    "match",
    ["get", "shapefileGeomId"]
  ];

  const fallbackImageName = ensureFillPatternImage(mapbox, pattern, fallbackColor);
  if (!fallbackImageName) return null;

  for (const f of features) {
    const featureColor = f.color ? getColor(f.color) : fallbackColor;
    const imageName = colorToImageName.get(featureColor);
    if (imageName && f.shapefileGeomId) {
      matchEntries.push(f.shapefileGeomId, imageName);
    }
  }

  // Fallback (default) image
  matchEntries.push(fallbackImageName);

  return matchEntries as unknown as mapboxgl.Expression;
}

interface ShapefileFeatureInput {
  shapefileGeomId?: string;
  checked?: boolean;
  properties?: Record<string, unknown>;
}

/**
 * Add shapefile layers to the map
 */
export function addShapetileLayers(
  mapbox: mapboxgl.Map,
  shapefile: Shapefile,
  shapefileFeatures:
    | ShapefileFeatureInput[]
    | Record<string, { features: ShapefileFeatureInput[] }>,
  layerList: IEditableProjectLayer[],
  addLayer,
  tileJSON?: TileJSONResponse // Optional pre-fetched TileJSON from batch request
) {
  const layerIdBase = shapefile.projectShapefileId || shapefile.shapefileId;

  const shapefileId = shapefile.shapefileId;
  const sourceId = layerIdBase;
  const layerId = `${layerIdBase}${shapefileLayerType.fill}`;
  const strokeLayerId = `${layerIdBase}${shapefileLayerType.stroke}`;
  const pointLayerId = `${layerIdBase}${shapefileLayerType.point}`;
  const labelLayerId = `${layerIdBase}${shapefileLayerType.label}`;

  if (!tileJSON || !tileJSON.tiles || tileJSON.tiles.length === 0) {
    return;
  }

  // Anchor this shapefile's layers under whatever should render above it, so a
  // re-added shapefile lands near its correct position immediately instead of
  // flashing on top of everything until the next layer-sort pass.
  const beforeId = getBeforeIdForNewShapefileLayers({
    mapbox,
    layerList,
    layerName: layerIdBase
  });

  mapbox.addSource(sourceId, {
    type: "vector",
    tiles: tileJSON.tiles,
    minzoom: tileJSON.minzoom,
    maxzoom: tileJSON.maxzoom,
    bounds: tileJSON.bounds
  });

  const geometryType = tileJSON.geometry_type || "Polygon";
  const layerType = getMapboxLayerType(geometryType);

  let featuresArray: ShapefileFeatureInput[];
  if (Array.isArray(shapefileFeatures)) {
    featuresArray = shapefileFeatures;
  } else if (shapefileFeatures && typeof shapefileFeatures === "object") {
    const featuresData = shapefileFeatures[shapefileId];
    if (featuresData && featuresData.features) {
      featuresArray = featuresData.features;
    } else {
      featuresArray = [];
    }
  } else {
    featuresArray = [];
  }

  const checkedFeatures = getCheckedFeatures(featuresArray, shapefile);

  const features = shapefile.features;

  // Create filter and default opacity
  const filter =
    checkedFeatures.length > 0
      ? ([
          "in",
          ["get", "shapefileGeomId"],
          ["literal", checkedFeatures]
        ] as mapboxgl.Expression)
      : null;

  const defaultOpacity = parseOpacity(shapefile.opacity);

  applyFeatureStates(mapbox, sourceId, shapefileId, features);
  // Use feature-state expressions (constant size regardless of feature count)
  const colorExpression: mapboxgl.Expression = [
    "coalesce",
    ["feature-state", "color"],
    "#3388ff"
  ] as mapboxgl.Expression;

  const strokeColorExpression: mapboxgl.Expression = [
    "coalesce",
    ["feature-state", "strokeColor"],
    "#000000"
  ] as unknown as mapboxgl.Expression;

  const thicknessExpression: mapboxgl.Expression = [
    "coalesce",
    ["feature-state", "thickness"],
    DEFAULT_SHAPEFILE_THICKNESS
  ] as unknown as mapboxgl.Expression;

  const opacityExpression: mapboxgl.Expression = [
    "coalesce",
    ["feature-state", "opacity"],
    defaultOpacity
  ] as unknown as mapboxgl.Expression;

  // Resolve dash array from shapefile's lineStyle (optimistic update) or layer list
  const dashArray = getDashArray(resolveShapefileLineStyle(shapefile, layerList));

  // Resolve fill pattern
  const fillPattern = normalizeFillPattern(
    resolveShapefileFillPattern(shapefile, layerList)
  );

  // Add appropriate layer type
  if (layerType === "fill" || layerType === "line") {
    const fillPaint: Record<string, unknown> = {
      "fill-color": colorExpression,
      "fill-opacity": opacityExpression
    };
    if (fillPattern !== "none") {
      const patternValue = buildFillPatternExpression(
        mapbox,
        fillPattern,
        features,
        getColor(features?.[0]?.color ?? "#3388ff")
      );
      if (patternValue) {
        fillPaint["fill-pattern"] = patternValue;
      }
    }
    addLayer(
      {
        id: layerId,
        type: "fill",
        source: sourceId,
        "source-layer": shapefileId,
        paint: fillPaint,
        ...(filter && { filter })
      },
      beforeId
    );
    addLayer(
      {
        id: strokeLayerId,
        type: "line",
        source: sourceId,
        "source-layer": shapefileId,
        paint: {
          "line-color": strokeColorExpression,
          "line-width": thicknessExpression,
          "line-dasharray": dashArray,
          "line-opacity": opacityExpression
        },
        ...(filter && { filter })
      },
      beforeId
    );
  } else if (layerType === "circle") {
    addLayer(
      {
        id: pointLayerId,
        type: "circle",
        source: sourceId,
        "source-layer": shapefileId,
        paint: {
          "circle-color": colorExpression,
          "circle-opacity": opacityExpression,
          "circle-stroke-color": strokeColorExpression,
          "circle-stroke-opacity": opacityExpression,
          "circle-stroke-width": 1,
          "circle-radius": buildShapefilePointRadius(thicknessExpression)
        },
        ...(filter && { filter })
      },
      beforeId
    );
  }

  const isShapefileLabelsLayerChecked =
    layerList.find((l) => l.name === SHAPEFILE_NAME_LABELS_LAYER)?.isChecked ?? false;

  const textOpacity = isShapefileLabelsLayerChecked
    ? checkedFeatures.length > 0
      ? ([
          "match",
          ["get", "shapefileGeomId"],
          checkedFeatures,
          1,
          0
        ] as mapboxgl.Expression)
      : 1
    : 0;

  const isLine = isLineGeometry(geometryType);

  const labelLayout: Record<string, unknown> = {
    "text-field": shapefile.layerName,
    "text-size": 9,
    "text-allow-overlap": false,
    "text-ignore-placement": false,
    "text-padding": 4,
    visibility: isShapefileLabelsLayerChecked ? "visible" : "none"
  };
  if (isLine) {
    labelLayout["symbol-placement"] = "line";
  } else {
    labelLayout["text-variable-anchor"] = ["top", "bottom", "left", "right"];
  }

  mapbox.addLayer(
    {
      id: labelLayerId,
      type: "symbol",
      source: sourceId,
      "source-layer": shapefileId,
      layout: labelLayout,
      paint: {
        "text-color": "#000",
        "text-halo-color": "#fff",
        "text-halo-width": 2,
        "text-opacity": textOpacity
      },
      ...(filter && { filter })
    },
    beforeId
  );
}

export function updateShapetileFilters(
  mapbox: mapboxgl.Map,
  shapefile: Shapefile,
  shapefileFeatures:
    | ShapefileFeatureInput[]
    | Record<string, { features: ShapefileFeatureInput[] }>,
  layerList: IEditableProjectLayer[]
): void {
  const layerIdBase = shapefile.projectShapefileId || shapefile.shapefileId;

  const shapefileId = shapefile.shapefileId;
  const sourceId = layerIdBase;
  const layerId = `${layerIdBase}${shapefileLayerType.fill}`;
  const strokeLayerId = `${layerIdBase}${shapefileLayerType.stroke}`;
  const labelLayerId = `${layerIdBase}${shapefileLayerType.label}`;
  const pointLayerId = `${layerIdBase}${shapefileLayerType.point}`;
  const propertyLabelLayerId = `${layerIdBase}${shapefileLayerType.property}`;

  const source = mapbox.getSource(sourceId);
  if (!source || (source as mapboxgl.VectorSource).type !== "vector") {
    return;
  }

  let featuresArray: ShapefileFeatureInput[];
  if (Array.isArray(shapefileFeatures)) {
    featuresArray = shapefileFeatures;
  } else if (shapefileFeatures && typeof shapefileFeatures === "object") {
    const featuresData = shapefileFeatures[layerIdBase] || shapefileFeatures[shapefileId];
    if (featuresData && featuresData.features) {
      featuresArray = featuresData.features;
    } else {
      featuresArray = [];
    }
  } else {
    featuresArray = [];
  }

  const checkedFeatures = getCheckedFeatures(featuresArray, shapefile);

  const featureFilter: mapboxgl.Expression = [
    "in",
    ["get", "shapefileGeomId"],
    ["literal", checkedFeatures]
  ] as mapboxgl.Expression;

  const features = shapefile.features;
  const defaultOpacity = parseOpacity(shapefile.opacity);

  applyFeatureStates(mapbox, sourceId, shapefileId, features);

  const colorExpression: mapboxgl.Expression = [
    "coalesce",
    ["feature-state", "color"],
    "#3388ff"
  ] as mapboxgl.Expression;

  const strokeColorExpression: mapboxgl.Expression = [
    "coalesce",
    ["feature-state", "strokeColor"],
    "#000000"
  ] as mapboxgl.Expression;

  const thicknessExpression: mapboxgl.Expression = [
    "coalesce",
    ["feature-state", "thickness"],
    DEFAULT_SHAPEFILE_THICKNESS
  ] as mapboxgl.Expression;

  const opacityExpression: mapboxgl.Expression = [
    "coalesce",
    ["feature-state", "opacity"],
    defaultOpacity
  ] as mapboxgl.Expression;

  // Update main layer (fill, line, or circle)
  if (mapbox.getLayer(layerId)) {
    setFilter(mapbox, layerId, featureFilter);
    setPaintProperty(mapbox, layerId, "fill-color", colorExpression);
    setPaintProperty(mapbox, layerId, "fill-opacity", opacityExpression);

    // Apply fill-pattern from shapefile's fillPattern (optimistic update) or layer list
    const updatedFillPattern = normalizeFillPattern(
      resolveShapefileFillPattern(shapefile, layerList)
    );
    if (updatedFillPattern !== "none") {
      const patternValue = buildFillPatternExpression(
        mapbox,
        updatedFillPattern,
        features,
        getColor(features?.[0]?.color ?? "#3388ff")
      );
      if (patternValue) {
        setPaintProperty(mapbox, layerId, "fill-pattern", patternValue);
      }
    } else {
      setPaintProperty(mapbox, layerId, "fill-pattern", "");
    }
  }
  if (mapbox.getLayer(pointLayerId)) {
    setFilter(mapbox, pointLayerId, featureFilter);
    setPaintProperty(mapbox, pointLayerId, "circle-color", colorExpression);
    setPaintProperty(mapbox, pointLayerId, "circle-opacity", opacityExpression);
    setPaintProperty(mapbox, pointLayerId, "circle-stroke-color", strokeColorExpression);
    setPaintProperty(mapbox, pointLayerId, "circle-stroke-opacity", opacityExpression);
    setPaintProperty(
      mapbox,
      pointLayerId,
      "circle-radius",
      buildShapefilePointRadius(thicknessExpression)
    );
  }
  if (mapbox.getLayer(strokeLayerId)) {
    setFilter(mapbox, strokeLayerId, featureFilter);
    setPaintProperty(mapbox, strokeLayerId, "line-color", strokeColorExpression);
    setPaintProperty(mapbox, strokeLayerId, "line-width", thicknessExpression);
    setPaintProperty(mapbox, strokeLayerId, "line-opacity", opacityExpression);

    // Apply line-dasharray from shapefile's lineStyle (optimistic update) or layer list
    setPaintProperty(
      mapbox,
      strokeLayerId,
      "line-dasharray",
      getDashArray(resolveShapefileLineStyle(shapefile, layerList))
    );
  }

  // Update label layer
  if (mapbox.getLayer(labelLayerId)) {
    setFilter(mapbox, labelLayerId, featureFilter);
  }

  // Update property label layer
  if (mapbox.getLayer(propertyLabelLayerId)) {
    setFilter(mapbox, propertyLabelLayerId, featureFilter);

    // Update text opacity to match checked features
    const textOpacity =
      checkedFeatures.length > 0
        ? ([
            "match",
            ["get", "shapefileGeomId"],
            checkedFeatures,
            1,
            0
          ] as mapboxgl.Expression)
        : 1;

    setPaintProperty(mapbox, propertyLabelLayerId, "text-opacity", textOpacity);
  }
}

// A malformed persisted value would otherwise put NaN or a negative width into feature state.
function parseThickness(rawThickness: unknown): number {
  const parsed = parseFloat(String(rawThickness));
  return Number.isFinite(parsed) ? Math.max(parsed, 0) : DEFAULT_SHAPEFILE_THICKNESS;
}

// The API can serve the string "null"; coalesce does not skip NaN, so it must never receive one.
function parseOpacity(rawOpacity: unknown): number {
  const parsed = parseFloat(String(rawOpacity));
  return Number.isFinite(parsed)
    ? Math.min(Math.max(parsed, 0), 1)
    : DEFAULT_SHAPEFILE_OPACITY;
}

function applyFeatureStates(
  mapbox: mapboxgl.Map,
  sourceId: string,
  sourceLayer: string,
  features: unknown
): void {
  if (!features || !Array.isArray(features) || features.length === 0) return;

  for (const f of features) {
    const feature = f as ShapefileFeatureRecord;

    const fid = feature.FID ?? feature.properties?.FID;
    if (fid === undefined) continue;

    const rawColor = feature.color ?? feature.properties?.color;
    const rawStrokeColor = feature.strokeColor ?? feature.properties?.strokeColor;
    const rawThickness = feature.thickness ?? feature.properties?.thickness;
    const rawOpacity = feature.opacity ?? feature.properties?.opacity;

    // Always set color, strokeColor, thickness with defaults to overwrite stale values.
    // For opacity: ONLY set it when the source data has a value. When null/undefined,
    // skip it so that setFeatureState (which MERGES) preserves any per-feature opacity
    // set by the slider via applyOpacityToMap. The paint expression's coalesce fallback
    // handles features that have never had opacity set.
    const style: FeatureStateStyle = {
      color:
        rawColor != null && rawColor !== "null" ? getColor(rawColor) : DEFAULT_FILL_COLOR,
      strokeColor:
        rawStrokeColor != null && rawStrokeColor !== "null"
          ? getColor(rawStrokeColor)
          : DEFAULT_STROKE_COLOR,
      thickness: parseThickness(rawThickness)
    };

    if (rawOpacity != null && String(rawOpacity) !== "null") {
      style.opacity = parseOpacity(rawOpacity);
    }

    mapbox.setFeatureState(
      { source: sourceId, sourceLayer: sourceLayer, id: fid },
      style
    );
  }
}

/**
 * Extracts checked feature IDs from shapefile features
 */
function getCheckedFeatures(
  featuresArray: ShapefileFeatureInput[],
  shapefile: Shapefile
): string[] {
  if (featuresArray.length > 0) {
    return featuresArray
      .filter((f) => {
        const isChecked = f.checked ?? f.properties?.checked;
        return isChecked !== false;
      })
      .map((f) => f.shapefileGeomId ?? f.properties?.shapefileGeomId)
      .filter(Boolean) as string[];
  } else if (shapefile.features?.length > 0) {
    return shapefile.features.map((f) => f.shapefileGeomId).filter(Boolean) as string[];
  } else {
    return [];
  }
}
